import { type ReactNode, createContext, use, useMemo } from "react";
import type { SectionEdit } from "@patent-studio/api-types";
import type { Send } from "./FrameworkPage";
import { type Lanes, SortableLanes } from "@/components/ui/SortableLanes";
import {
  SECTIONS,
  type UnplacedItem,
  frameworkLanes,
  moveAsDragged,
  placeUnplaced,
} from "./framework-edits";

const MOVED_AWAY =
  "What you moved, or the section you moved it to, is gone. Nothing was moved.";

const ArrangedContext = createContext<Lanes | null>(null);

/** The framework's order as a drag is showing it, which runs ahead of the server's while one is on. */
export function useArrangedLanes(edits: SectionEdit[]): Lanes {
  return use(ArrangedContext) ?? frameworkLanes(edits);
}

/** The id an unplaced item is carried under, and the id a section is landed on by. */
export function carriedId(item: UnplacedItem): string {
  return `unplaced:${item.kind}:${item.id}`;
}
export function intoSection(sectionId: string): string {
  return `into:${sectionId}`;
}

function carriedItem(id: string): UnplacedItem | null {
  const [prefix, kind, ...rest] = id.split(":");
  if (prefix !== "unplaced" || (kind !== "figure" && kind !== "claim")) {
    return null;
  }
  return { kind, id: rest.join(":") };
}

/**
 * The framework as something the attorney rearranges by hand: sections among
 * themselves, subsections within and across sections, and an unplaced figure
 * or claim carried onto the section it should join. Each finished drag sends
 * the one move, made against the framework as it is when the write goes, so
 * nothing a write queued before it added is lost; a refused one puts the rows
 * back.
 */
export function FrameworkDrag({
  edits,
  name,
  carried,
  send,
  children,
}: {
  edits: SectionEdit[];
  /** How a reader hears an id: a section's title, a line's words, a figure's label. */
  name: (id: string) => string;
  carried: (id: string) => ReactNode;
  /** Sends an edit made against the latest framework; `putBack` restores the rows if it is refused. */
  send: Send;
  children: ReactNode;
}) {
  const served = useMemo(() => frameworkLanes(edits), [edits]);
  return (
    <SortableLanes
      served={served}
      name={(id) =>
        String(id) === SECTIONS ? "the sections" : name(String(id))
      }
      lands={["unplaced"]}
      carried={carried}
      onArrange={(lanes, putBack, moved) => {
        if (moved === undefined) return;
        send((latest) => moveAsDragged(latest, lanes, moved), {
          refused: MOVED_AWAY,
          putBack,
          control: "drag",
        });
      }}
      onLand={(landing) => {
        const item = carriedItem(landing.source);
        const sectionId = landing.target.replace(/^into:/, "");
        if (item === null || sectionId === landing.target) return;
        const id = crypto.randomUUID();
        send((latest) => placeUnplaced(latest, item, sectionId, id), {
          control: "drag unplaced",
        });
      }}
    >
      {({ lanes }) => (
        <ArrangedContext value={lanes}>{children}</ArrangedContext>
      )}
    </SortableLanes>
  );
}
